import { Food } from "@corpuscomplete/core/nutrition/food";
import { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { useEffect, useRef, useState } from "react";
import { FoodItemLabel } from "@corpuscomplete/web/components/nutrition/food-item";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@corpuscomplete/web-ui/components/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@corpuscomplete/web-ui/components/tooltip";
import {
  SearchComboboxBaseEmpty,
  SearchComboboxBaseItem,
  type SearchComboboxItemValue,
  SearchComboboxBaseRoot,
  SearchComboboxBaseSection,
} from "@corpuscomplete/web-ui/components/search-combobox-base";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { Maximize2Icon } from "lucide-react";

const MIN_SEARCH_LENGTH = 3;

function stopPointerEvent(event: React.MouseEvent | React.PointerEvent) {
  event.preventDefault();
  event.stopPropagation();
}

function isComboboxInteractionTarget(target: EventTarget | null) {
  return target instanceof Element && target.closest('[role="listbox"]') !== null;
}

function isPreparationInteractionTarget(target: EventTarget | null) {
  return target instanceof Element && target.closest("[data-preparation-interaction]") !== null;
}

function PreparationPopover({
  label,
  preparations,
  isOpen,
  onOpenChange,
  onSelect,
  onCloseToInput,
  autoFocusFirstItem,
  onAutoFocusHandled,
}: {
  label: string;
  preparations: FoodSearchUI.Row["preparations"];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: Food.Item) => void;
  onCloseToInput?: () => void;
  autoFocusFirstItem?: boolean;
  onAutoFocusHandled?: () => void;
}) {
  const preparationButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!isOpen || !autoFocusFirstItem) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      preparationButtonRefs.current[0]?.focus();
      onAutoFocusHandled?.();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [autoFocusFirstItem, isOpen, onAutoFocusHandled]);

  const focusPreparationButton = (index: number) => {
    const totalButtons = preparations.length;
    if (totalButtons === 0) {
      return;
    }

    const nextIndex = (index + totalButtons) % totalButtons;
    preparationButtonRefs.current[nextIndex]?.focus();
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={label}
            data-preparation-interaction
            className={cn("pointer-events-auto relative shrink-0", isOpen && "z-30")}
            onClick={(event) => {
              stopPointerEvent(event);
            }}
            onMouseDown={stopPointerEvent}
            onPointerDown={stopPointerEvent}
          />
        }
      >
        <Badge
          variant="outline"
          className="bg-transparent hover:bg-gray-100 [[data-slot=popover-trigger][data-popup-open]_&]:bg-gray-100"
        >
          {label}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        initialFocus={false}
        className="w-max min-w-max max-w-[calc(100vw-1rem)] gap-0 p-1"
        data-preparation-interaction
      >
        {preparations.map((preparation, index) => (
          <button
            type="button"
            key={preparation.key}
            ref={(element) => {
              preparationButtonRefs.current[index] = element;
            }}
            data-preparation-interaction
            data-preparation-option
            className="focus:bg-accent focus:text-accent-foreground flex min-h-7 w-full cursor-default items-center rounded-md px-2 py-1 text-left text-xs/relaxed outline-hidden hover:bg-accent hover:text-accent-foreground"
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                event.stopPropagation();
                focusPreparationButton(index + 1);
                return;
              }

              if (event.key === "ArrowUp") {
                event.preventDefault();
                event.stopPropagation();
                focusPreparationButton(index - 1);
                return;
              }

              if (event.key === "ArrowLeft" || event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                onCloseToInput?.();
                onOpenChange(false);
                return;
              }

              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                onSelect(preparation.item);
              }
            }}
            onMouseDown={stopPointerEvent}
            onClick={(event) => {
              stopPointerEvent(event);
              onSelect(preparation.item);
            }}
          >
            <FoodItemLabel item={preparation.item} mode="preparation" />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function FoodSearchCombobox({
  onSelect,
  placeholder = "Search foods...",
  portalContainer,
  onOpenAdvancedSearch,
}: {
  onSelect: (item: Food.Item) => void;
  placeholder?: string;
  portalContainer?: HTMLElement | null;
  onOpenAdvancedSearch?: (args: { text: string }) => void;
}) {
  const [text, setText] = useState("");
  const { sections, isLoading } = FoodSearchUI.useSearch({ text });
  const [isDismissedAfterSelection, setIsDismissedAfterSelection] = useState(false);
  const [openPreparationRowKey, setOpenPreparationRowKey] = useState<string | null>(null);
  const [autoFocusPreparationRowKey, setAutoFocusPreparationRowKey] = useState<string | null>(null);
  const preserveBlurRef = useRef(false);
  const restoreInputFocusRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hasResults = sections.some((section) => section.data.length > 0);
  const isShortQuery = text.length > 0 && text.length < MIN_SEARCH_LENGTH;
  const shouldSearch = text.length >= MIN_SEARCH_LENGTH;
  const open = !isDismissedAfterSelection && (isShortQuery || isLoading || shouldSearch);
  const itemsByKey = new Map<string, Food.Item>();

  for (const section of sections) {
    for (const row of section.data) {
      itemsByKey.set(row.key, row.item);
      for (const preparation of row.preparations) {
        itemsByKey.set(preparation.key, preparation.item);
      }
    }
  }

  const getItemValue = (key: string, item: Food.Item): SearchComboboxItemValue => ({
    value: key,
    label: Food.formatItem(item).title,
  });

  const rowsByKey = new Map<string, FoodSearchUI.Row>();

  for (const section of sections) {
    for (const row of section.data) {
      rowsByKey.set(row.key, row);
    }
  }

  const closePreparationPopover = () => {
    setOpenPreparationRowKey(null);
    setAutoFocusPreparationRowKey(null);
    restoreInputFocusRef.current = false;
  };

  const selectItem = (item: Food.Item) => {
    setIsDismissedAfterSelection(true);
    closePreparationPopover();
    onSelect(item);
    setText("");

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const clearSearch = () => {
    setIsDismissedAfterSelection(false);
    closePreparationPopover();
    setText("");
  };

  useEffect(() => {
    const onPointerDownCapture = (event: PointerEvent) => {
      preserveBlurRef.current =
        isPreparationInteractionTarget(event.target) || isComboboxInteractionTarget(event.target);
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
    };
  }, []);

  const openPreparationPopoverFromKeyboard = () => {
    const highlightedOption = document.querySelector<HTMLElement>(
      '[role="option"][data-highlighted][data-row-key]',
    );
    const rowKey = highlightedOption?.dataset.rowKey;

    if (!rowKey) {
      return false;
    }

    const row = rowsByKey.get(rowKey);
    if (!row || row.preparations.length === 0) {
      return false;
    }

    preserveBlurRef.current = true;
    restoreInputFocusRef.current = true;
    setAutoFocusPreparationRowKey(row.key);
    setOpenPreparationRowKey(row.key);
    return true;
  };

  const combobox = (
    <SearchComboboxBaseRoot
      inputRef={inputRef}
      value={text}
      onValueChange={(nextText) => {
        setIsDismissedAfterSelection(false);
        closePreparationPopover();
        setText(nextText);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();

          if (openPreparationRowKey !== null) {
            closePreparationPopover();
            inputRef.current?.focus();
            return;
          }

          if (open) {
            clearSearch();
            inputRef.current?.focus();
          }

          return;
        }

        if (event.key !== "ArrowRight" || openPreparationRowKey !== null || !open) {
          return;
        }

        const input = event.currentTarget;
        const caretAtEnd =
          input.selectionStart === input.value.length && input.selectionEnd === input.value.length;

        if (!caretAtEnd) {
          return;
        }

        if (!openPreparationPopoverFromKeyboard()) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
      }}
      onBlur={() => {
        window.setTimeout(() => {
          const shouldPreserve =
            preserveBlurRef.current ||
            isPreparationInteractionTarget(document.activeElement) ||
            isComboboxInteractionTarget(document.activeElement);
          preserveBlurRef.current = false;
          if (shouldPreserve) {
            return;
          }
          clearSearch();
        }, 0);
      }}
      onSelect={(selected) => {
        const item = itemsByKey.get(selected.value);
        if (!item) {
          return;
        }
        selectItem(item);
      }}
      placeholder={placeholder}
      portalContainer={portalContainer}
      open={open}
    >
      {isShortQuery ? (
        <SearchComboboxBaseEmpty>
          Type at least {MIN_SEARCH_LENGTH} characters to search.
        </SearchComboboxBaseEmpty>
      ) : null}
      {isLoading && !hasResults ? (
        <SearchComboboxBaseEmpty>Loading...</SearchComboboxBaseEmpty>
      ) : null}
      {!isLoading && !hasResults && text.length >= MIN_SEARCH_LENGTH ? (
        <SearchComboboxBaseEmpty>No results found</SearchComboboxBaseEmpty>
      ) : null}
      {sections.map((section) =>
        section.data.length > 0 ? (
          <SearchComboboxBaseSection
            key={section.title}
            title={section.title}
            expandButton={section.expandButton}
          >
            {section.data.map((row) => {
              const isPreparationMenuOpenForRow = openPreparationRowKey === row.key;

              return (
                <SearchComboboxBaseItem
                  key={row.key}
                  value={getItemValue(row.key, row.item)}
                  rowKey={row.key}
                  disabled={openPreparationRowKey !== null}
                  className={cn(
                    "relative flex items-start justify-between gap-2",
                    "data-[highlighted]:has-[button:hover]:bg-transparent",
                    "data-[highlighted]:has-[button[data-state=open]]:bg-transparent",
                    isPreparationMenuOpenForRow && "data-[highlighted]:bg-transparent",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <FoodItemLabel item={row.item} />
                    {row.preparations.length > 0 ? (
                      <span className="sr-only">
                        Press Right Arrow to open preparation options.
                      </span>
                    ) : null}
                  </div>
                  {row.preparations.length > 0 ? (
                    <PreparationPopover
                      label={row.preparationCountLabel ?? "Preparations"}
                      preparations={row.preparations}
                      isOpen={isPreparationMenuOpenForRow}
                      onCloseToInput={() => {
                        restoreInputFocusRef.current = false;
                        inputRef.current?.focus();
                      }}
                      onOpenChange={(isOpen) => {
                        if (
                          !isOpen &&
                          restoreInputFocusRef.current &&
                          openPreparationRowKey === row.key
                        ) {
                          inputRef.current?.focus();
                          restoreInputFocusRef.current = false;
                        }

                        setOpenPreparationRowKey(isOpen ? row.key : null);
                      }}
                      onSelect={selectItem}
                      autoFocusFirstItem={autoFocusPreparationRowKey === row.key}
                      onAutoFocusHandled={() => {
                        setAutoFocusPreparationRowKey((currentRowKey) =>
                          currentRowKey === row.key ? null : currentRowKey,
                        );
                      }}
                    />
                  ) : null}
                </SearchComboboxBaseItem>
              );
            })}
          </SearchComboboxBaseSection>
        ) : null,
      )}
    </SearchComboboxBaseRoot>
  );

  if (!onOpenAdvancedSearch) {
    return combobox;
  }

  return (
    <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">{combobox}</div>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label="Open full food search"
              className="flex size-7 shrink-0 items-center justify-center rounded-md border border-input bg-input/20 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              onClick={() => onOpenAdvancedSearch({ text })}
            />
          }
        >
          <Maximize2Icon className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent>Advanced food search</TooltipContent>
      </Tooltip>
    </div>
  );
}
